/**
 * Libp2pMesh — js-libp2p GossipSub mesh client
 *
 * Replaces LobbyGossip.ts WebSocket relay.
 * Connects to Go Lighthouse via WebSocket transport.
 * Subscribes to AETHER_LOBBY_V1 via GossipSub.
 * Falls back to legacy WebSocket if libp2p fails.
 */

import { createLibp2p, type Libp2p } from 'libp2p';
import { webSockets } from '@libp2p/websockets';
import { webRTC } from '@libp2p/webrtc';
import { noise } from '@chainsafe/libp2p-noise';
import { yamux } from '@libp2p/yamux';
import { gossipsub, type GossipSub } from '@chainsafe/libp2p-gossipsub';
import { identify } from '@libp2p/identify';
import { circuitRelayTransport } from '@libp2p/circuit-relay-v2';
import { bootstrap } from '@libp2p/bootstrap';
import { multiaddr } from '@multiformats/multiaddr';
import type { LobbyMessage } from './LobbyClient';

const TOPIC = 'AETHER_LOBBY_V1';

// Go relay multiaddr from env — e.g. /ip4/1.2.3.4/tcp/9877/ws/p2p/12D3KooW...
const RELAY_MULTIADDR = import.meta.env.VITE_MESH_MULTIADDR || '';

export type MeshHandler = (msg: LobbyMessage) => void;
export type MeshStatusHandler = (connected: boolean, peerCount: number) => void;

class Libp2pMeshClient {
  private static instance: Libp2pMeshClient;
  private node: Libp2p | null = null;
  private handlers: Set<MeshHandler> = new Set();
  private statusHandlers: Set<MeshStatusHandler> = new Set();
  private connected = false;
  private nodeId = '';
  private nodeName = '';

  static getInstance(): Libp2pMeshClient {
    if (!Libp2pMeshClient.instance) {
      Libp2pMeshClient.instance = new Libp2pMeshClient();
    }
    return Libp2pMeshClient.instance;
  }

  async connect(nodeId: string, nodeName: string): Promise<void> {
    this.nodeId = nodeId;
    this.nodeName = nodeName;

    if (!RELAY_MULTIADDR) {
      console.warn('[Libp2pMesh] No VITE_MESH_MULTIADDR — cannot start');
      return;
    }

    try {
      this.node = await createLibp2p({
        transports: [
          webSockets(),               // Dial Go relay via WS
          webRTC(),                    // Direct browser-to-browser
          circuitRelayTransport(),     // Hop through Go relay
        ],
        connectionEncrypters: [noise()],
        streamMuxers: [yamux()],
        peerDiscovery: [
          bootstrap({
            list: [RELAY_MULTIADDR],  // Bootstrap to Go lighthouse
          }),
        ],
        services: {
          identify: identify(),
          pubsub: gossipsub({
            allowPublishToZeroTopicPeers: true,
            emitSelf: false,
          }) as any, // eslint-disable-line @typescript-eslint/no-explicit-any — interface version mismatch workaround
        },
      });

      await this.node.start();

      console.log('[Libp2pMesh] PeerId:', this.node.peerId.toString());

      // Dial the Go relay explicitly
      const relayMa = multiaddr(RELAY_MULTIADDR);
      await this.node.dial(relayMa);
      console.log('[Libp2pMesh] Connected to relay:', RELAY_MULTIADDR);

      // Subscribe to GossipSub topic
      const ps = this.node.services.pubsub as GossipSub;
      ps.subscribe(TOPIC);

      ps.addEventListener('message', (evt) => {
        if (evt.detail.topic !== TOPIC) return;

        try {
          const msg: LobbyMessage = JSON.parse(
            new TextDecoder().decode(evt.detail.data)
          );
          if (msg && msg.id) {
            this.handlers.forEach((h) => h(msg));
          }
        } catch {
          // Malformed — skip
        }
      });

      this.connected = true;
      this.notifyStatus();

      // Monitor peer count
      this.node.addEventListener('peer:connect', () => this.notifyStatus());
      this.node.addEventListener('peer:disconnect', () => this.notifyStatus());

    } catch (err) {
      console.error('[Libp2pMesh] Init failed:', err);
      this.connected = false;
      this.notifyStatus();
    }
  }

  async disconnect(): Promise<void> {
    if (this.node) {
      await this.node.stop();
      this.node = null;
    }
    this.connected = false;
    this.notifyStatus();
  }

  publish(msg: {
    content: string;
    reply_to?: string;
    commitment?: string;
    chain_hash?: string;
    seq?: number;
  }): void {
    if (!this.node || !this.connected) return;

    const lobbyMsg: LobbyMessage = {
      id: crypto.randomUUID(),
      node_id: this.nodeId,
      node_name: this.nodeName,
      content: msg.content,
      reply_to: msg.reply_to ?? '',
      timestamp: Date.now(),
      commitment: msg.commitment ?? '',
      chain_hash: msg.chain_hash ?? '',
      seq: msg.seq ?? 0,
    };

    const ps = this.node.services.pubsub as GossipSub;
    const data = new TextEncoder().encode(JSON.stringify(lobbyMsg));
    ps.publish(TOPIC, data).catch((err: Error) => {
      console.warn('[Libp2pMesh] Publish failed:', err);
    });
  }

  subscribe(handler: MeshHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  onStatus(handler: MeshStatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  isConnected(): boolean {
    return this.connected;
  }

  getPeerCount(): number {
    return this.node?.getPeers().length ?? 0;
  }

  getPeerId(): string {
    return this.node?.peerId.toString() ?? '';
  }

  private notifyStatus(): void {
    const count = this.getPeerCount();
    this.statusHandlers.forEach((h) => h(this.connected, count));
  }
}

export const libp2pMesh = Libp2pMeshClient.getInstance();
export type { LobbyMessage };
